import { useUserSupportThreads, SupportThread } from "@/hooks/useSupportThreads";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";
import { MessageCircle, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface RequestListProps {
  onSelectThread: (threadId: string) => void;
}

const STATUS_CONFIG = {
  open: {
    label: "Open",
    variant: "default" as const,
    icon: MessageCircle,
    className: "bg-blue-100 text-blue-800 hover:bg-blue-100",
  },
  awaiting_user: {
    label: "Awaiting You",
    variant: "secondary" as const,
    icon: AlertCircle,
    className: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  },
  resolved: {
    label: "Resolved",
    variant: "outline" as const,
    icon: CheckCircle,
    className: "bg-green-100 text-green-800 hover:bg-green-100",
  },
};

export function RequestList({ onSelectThread }: RequestListProps) {
  const { data: threads, isLoading } = useUserSupportThreads();

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  if (!threads?.length) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No support requests yet</p>
        <p className="text-sm mt-1">Create a new request to get help</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
      {threads.map((thread) => (
        <ThreadCard
          key={thread.id}
          thread={thread}
          onClick={() => onSelectThread(thread.id)}
        />
      ))}
    </div>
  );
}

function ThreadCard({
  thread,
  onClick,
}: {
  thread: SupportThread;
  onClick: () => void;
}) {
  const status = STATUS_CONFIG[thread.status] || STATUS_CONFIG.open;
  const StatusIcon = status.icon;
  const hasUnread = (thread.unread_count || 0) > 0;

  return (
    <Card
      className={cn(
        "p-4 cursor-pointer transition-colors hover:bg-muted/50",
        hasUnread && "border-primary/50 bg-primary/5"
      )}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge className={cn("text-xs", status.className)}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {status.label}
            </Badge>
            {hasUnread && (
              <Badge variant="destructive" className="text-xs">
                {thread.unread_count} new
              </Badge>
            )}
          </div>
          <p className="font-medium truncate">
            {thread.subject || "Support Request"}
          </p>
          <p className="text-sm text-muted-foreground truncate">
            {thread.last_message_preview}
          </p>
        </div>
        <div className="flex flex-col items-end text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatDistanceToNow(new Date(thread.last_message_at), { addSuffix: true })}
          </div>
          {thread.category && (
            <span className="capitalize mt-1">{thread.category}</span>
          )}
        </div>
      </div>
    </Card>
  );
}
